import React from 'react';
import { FieldOption } from '../types/form.types';
import { FormTheme } from '../types/formTheme';

interface FormRadioProps {
    id: string;
    name: string;
    value: string;
    onChange: (value: string) => void;
    options: FieldOption[];
    required?: boolean;
    disabled?: boolean;
    error?: string;
    appearance?: FormTheme;
    className?: string;
}

const FormRadio: React.FC<FormRadioProps> = ({
    id,
    name,
    value,
    onChange,
    options,
    required = false,
    disabled = false,
    error,
    appearance = 'default',
    className = ''
}) => {
    return (
        <div className={`space-y-3 ${className}`} role="radiogroup" aria-invalid={error ? 'true' : 'false'}>
            {options.map((option) => {
                const optionId = `${id}-${option.value}`;
                const isSelected = value === option.value;

                return (
                    <label
                        key={option.value}
                        htmlFor={optionId}
                        className={`
                            ${appearance === 'tracking'
                                ? 'flex items-center gap-3 p-4 rounded-lg bg-white border transition-all duration-200'
                                : 'flex items-center gap-3 p-4 rounded-xl bg-white/80 backdrop-blur-sm border-2 transition-all duration-200'
                            }
                            cursor-pointer
                            ${disabled ? 'opacity-50 cursor-not-allowed' : appearance === 'tracking' ? 'hover:border-gray-400' : 'hover:border-blue-300'}
                            ${isSelected
                                ? appearance === 'tracking'
                                    ? 'border-black bg-gray-50'
                                    : 'border-blue-500 bg-blue-50/50'
                                : error
                                    ? 'border-red-400'
                                    : appearance === 'tracking'
                                        ? 'border-gray-300'
                                        : 'border-gray-200'
                            }
                        `}
                    >
                        <input
                            type="radio"
                            id={optionId}
                            name={name}
                            value={option.value}
                            checked={isSelected}
                            onChange={(e) => onChange(e.target.value)}
                            required={required}
                            disabled={disabled}
                            className="sr-only"
                        />
                        <div
                            className={`
                                w-5 h-5 rounded-full border-2 flex items-center justify-center
                                transition-all duration-200 flex-shrink-0
                                ${isSelected
                                    ? appearance === 'tracking'
                                        ? 'border-black bg-black'
                                        : 'border-blue-500 bg-blue-500'
                                    : 'border-gray-300'
                                }
                            `}
                        >
                            {isSelected && (
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                        </div>
                        <span className="text-gray-900 font-medium">{option.label}</span>
                    </label>
                );
            })}
        </div>
    );
};

export default FormRadio;
